import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Lock, Loader2, ArrowLeft } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("error") === "not_admin") {
      toast.error("Your account does not have admin privileges.");
    }
  }, [searchParams]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) return;
      if (session.user?.user_metadata?.role === "admin") {
        navigate("/admin", { replace: true });
      }
    });
  }, [navigate]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (authError) throw authError;
      navigate("/admin");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotPassword(e: React.FormEvent) {
    e.preventDefault();
    if (!email) {
      setError("Please enter your email address.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { error: resetErr } = await supabase.auth.resetPasswordForEmail(
        email,
        { redirectTo: `${window.location.origin}/admin/login` },
      );
      if (resetErr) throw resetErr;
      toast.success("Password reset email sent. Check your inbox.");
      setForgotMode(false);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to send reset email",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <form
        onSubmit={forgotMode ? handleForgotPassword : handleSubmit}
        className="bg-white rounded-2xl shadow-lg border p-8 w-full max-w-sm"
      >
        <div className="flex justify-center mb-6">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
            <Lock className="w-7 h-7 text-primary" />
          </div>
        </div>
        <h1 className="text-xl font-bold text-center text-gray-900 mb-1">
          {forgotMode ? "Reset Password" : "Vita Dom Admin"}
        </h1>
        <p className="text-sm text-gray-500 text-center mb-6">
          {forgotMode
            ? "Enter your email to receive a reset link"
            : "Sign in with your admin account"}
        </p>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          autoFocus
          required
          className="w-full rounded-lg border border-gray-200 px-4 py-3 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none mb-3"
        />
        {!forgotMode && (
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            required
            minLength={6}
            className={`w-full rounded-lg border px-4 py-3 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none mb-4 transition ${
              error ? "border-red-400 bg-red-50" : "border-gray-200"
            }`}
          />
        )}
        {error && (
          <p className="text-red-500 text-xs mb-3 text-center">{error}</p>
        )}
        <Button type="submit" className="w-full" size="lg" disabled={loading}>
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : null}
          {forgotMode ? "Send Reset Link" : "Sign In"}
        </Button>
        <div className="text-center mt-4">
          {forgotMode ? (
            <button
              type="button"
              onClick={() => {
                setForgotMode(false);
                setError("");
              }}
              className="text-xs text-primary hover:underline inline-flex items-center gap-1"
            >
              <ArrowLeft className="w-3 h-3" />
              Back to sign in
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                setForgotMode(true);
                setError("");
              }}
              className="text-xs text-gray-400 hover:text-primary hover:underline transition"
            >
              Forgot password?
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
